package com.patronat.top.features.usermanagement.presentation.create

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.widthIn
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.patronat.top.dto.UserRole
import com.patronat.top.features.usermanagement.data.mappers.toDomain
import com.patronat.top.features.usermanagement.data.mappers.toDto
import com.patronat.top.features.usermanagement.domain.models.UserStatus
import com.patronat.top.library.media.data.KmpFileSource
import com.patronat.top.library.media.utils.ImageCompressor
import com.patronat.top.ui.organisms.user.UserFormContent
import com.patronat.top.ui.organisms.user.UserFormData
import io.github.vinceglb.filekit.FileKit
// FileKit Imports
import io.github.vinceglb.filekit.dialogs.FileKitType
import io.github.vinceglb.filekit.dialogs.openFilePicker
import io.github.vinceglb.filekit.name
import kotlinx.coroutines.launch
import org.koin.compose.koinInject

@Composable
actual fun CreateUserScreen(
    modalTitle: String,
    targetRole: UserRole,
    userId: String?,
    onBack: () -> Unit
) {
    val viewModel = koinInject<CreateUserViewModel>()

    // 1. Стан ViewModel
    val state by viewModel.viewState.collectAsState()

    // 2. Ініціалізація ролі при першому вході
    LaunchedEffect(targetRole, userId) {
        viewModel.onEvent(CreateOrUpdateUserEvent.Initialize(targetRole, userId))
    }

    // 3. Обробка ефектів (Side Effects)
    LaunchedEffect(Unit) {
        viewModel.effect.collect { effect ->
            when (effect) {
                CreateUpdateUserEffect.CloseScreen -> onBack()
                CreateUpdateUserEffect.OpenImagePicker -> {
                    launch {
                        try {
                            val file = FileKit.openFilePicker(
                                type = FileKitType.Image,
                                title = "Оберіть аватар"
                            )

                            file?.let { platformFile ->
                                val rawBytes = ImageCompressor.compressAndCrop(file = platformFile)

                                val fileSource = KmpFileSource(
                                    name = platformFile.name,
                                    bytes = rawBytes,
                                    platformSource = platformFile
                                )
                                viewModel.onEvent(CreateOrUpdateUserEvent.OnAvatarSelected(fileSource))
                            }
                        } catch (e: Exception) {
                            println("Error picking image: ${e.message}")
                        }
                    }
                }
                is CreateUpdateUserEffect.ShowError -> {
                    println("Помилка: ${effect.message}")
                }
            }
        }
    }

    // 4. Підготовка даних
    val statusOptions = remember { UserStatus.entries.map { it.toUiItem() } }
    val currentUiStatus = statusOptions.firstOrNull { it.value == state.status.toDomain() } ?: statusOptions.first()

    val formData = UserFormData(
        avatarUrl = state.avatarUrl,
        firstName = state.firstName,
        lastName = state.lastName,
        email = state.email,
        phone = state.phone,
        statusItem = currentUiStatus,
        isLoading = state.isLoading || state.isAvatarUploading
    )


    Dialog(
        onDismissRequest = onBack,

        properties = DialogProperties(
            usePlatformDefaultWidth = false,
            dismissOnBackPress = true,
            dismissOnClickOutside = true
        )
    ) {

        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(Color.Black.copy(alpha = 0.5f)),
            contentAlignment = Alignment.Center
        ) {
            UserFormContent(
                isCreatedUser = userId == null,
                title = modalTitle,
                state = formData,
                statusOptions = statusOptions,
                modifier = Modifier.widthIn(max = 550.dp),

                onFieldChange = { newData ->
                    viewModel.onEvent(CreateOrUpdateUserEvent.OnDataChanged(newData.firstName, newData.lastName, newData.email, newData.phone))
                },
                onStatusChange = { newStatus ->
                    viewModel.onEvent(CreateOrUpdateUserEvent.OnStatusChanged(newStatus.toDto()))
                },
                onAvatarClick = { viewModel.onEvent(CreateOrUpdateUserEvent.OnAvatarClicked) },
                onSaveClick = { viewModel.onEvent(CreateOrUpdateUserEvent.OnSubmitClicked) },
                onCancelClick = onBack
            )
        }
    }
}